import React, { useState, useCallback, useEffect } from 'react';
import { Card, Table, Form, Button, InputGroup, Modal } from 'react-bootstrap';
import { PlusCircleFill, Search, TrashFill } from 'react-bootstrap-icons';
import axios from 'axios';
import AlertsFacturas from './Utils/AlertsFacturasPedidos';
import ProductSearchModal from './ProductSearchModal';
import APIserver from '../../../../api/axiosConfig';
// Función para formatear en pesos colombianos
const formatCOP = (value) => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

const ProductoRow = React.memo(({ producto, onChange, onRemove, onSearch, onKeyDown, onBlurCantidad }) => {
    const handleChange = (e) => {
        const { name, value } = e.target;
        onChange(producto.id, name, value);
    };

    const handleSearchClick = () => {
        onSearch(producto.id);
    };

    return (
        <tr id={`row-${producto.id}`}>
            <td>
                <Form.Control
                    type="text"
                    name="referencia"
                    value={producto.referencia}
                    onChange={handleChange}
                    onKeyDown={(e) => onKeyDown(e, producto.id)}
                    placeholder="Referencia"
                />
            </td>
            <td>
                <InputGroup>
                    <Form.Control
                        type="text"
                        name="nomProducto"
                        value={producto.nomProducto}
                        onChange={handleChange}
                        placeholder="Nombre"
                        readOnly
                    />
                    <Button variant='outline-dark' size='sm' title='Buscar producto' onClick={handleSearchClick}>
                        <Search />
                    </Button>
                </InputGroup>
            </td>
            <td>
                <Form.Control
                    type="number"
                    name="cantidad"
                    value={producto.cantidad}
                    onChange={handleChange}
                    onBlur={() => onBlurCantidad(producto.id, producto.cantidad)}  // Usa la función de cantidad en onBlur
                    placeholder="Cantidad"
                />
                {producto.stockErrorMessage && (
                    <div style={{ color: 'red', marginTop: '5px' }}>
                        {producto.stockErrorMessage}
                    </div>
                )}
            </td>
            <td>
                <Form.Control
                    type="text"
                    name="valorUnitario"
                    value={formatCOP(parseFloat(producto.valorUnitario) || 0)}
                    onChange={handleChange}
                    placeholder="Valor Unitario"
                    readOnly
                />
            </td>
            <td>
                <Form.Control
                    type="text"
                    value={formatCOP((parseFloat(producto.cantidad) * parseFloat(producto.valorUnitario) || 0))}
                    placeholder="Total"
                    disabled
                />
            </td>
            <td>
                <Button variant='danger' onClick={() => onRemove(producto.id)}>
                    <TrashFill />
                </Button>
            </td>
        </tr>
    );
});

const Productos = ({ setProductos, setSubtotal, setTotal, setCantItem, setEnvio, tipoFactura, productos, costoEnvio, descuento, setDescuento }) => {

    const [localProductos, setLocalProductos] = useState(productos || [{
        id: Date.now(),
        referencia: '',
        nomProducto: '',
        cantidad: 1,
        valorUnitario: 0,
        autoCompleted: false,
        stockErrorMessage: '',  // Agregamos el mensaje de error aquí
    }]);
    const [localEnvio, setLocalEnvio] = useState(0); // Inicializa en 0 para ingreso manual
    const [showModal, setShowModal] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentProductId, setCurrentProductId] = useState(null);
    const [isAddingRow, setIsAddingRow] = useState(false);
    const [localDescuento, setLocalDescuento] = useState(descuento);
    const [stockErrorMessage, setStockErrorMessage] = useState('');

    useEffect(() => {
        if (productos && Array.isArray(productos) && productos.length > 0) {
            setLocalProductos(productos);
        }
    }, [productos]);

    useEffect(() => {
        if (costoEnvio !== undefined) {
            setLocalEnvio(costoEnvio);
        }
    }, [costoEnvio]);

    useEffect(() => {
        const envioNum = parseFloat(localEnvio) || 0;
        setEnvio(envioNum);
    }, [localEnvio, setEnvio]);

    useEffect(() => {
        if (descuento !== undefined) {
            setLocalDescuento(descuento);
        }
    }, [descuento]);

    useEffect(() => {
        if (!isNaN(localDescuento)) {
            setDescuento(parseFloat(localDescuento));
        }
    }, [localDescuento, setDescuento]);
    useEffect(() => {
        // Asegura que siempre que cambien los productos locales, se actualice el estado de productos en el padre
        setProductos(localProductos);
    }, [localProductos, setProductos]);


    const handleChangeCantidadDirecta = (id, cantidad) => {
        // Convertir cantidad a un número para evitar errores con strings
        const cantidadNum = parseFloat(cantidad) || 0;

        // Buscar el producto correspondiente y actualizar la cantidad
        const updatedProductos = localProductos.map(p =>
            p.id === id ? { ...p, cantidad: cantidadNum } : p
        );

        setLocalProductos(updatedProductos); // Actualiza el estado local
        setProductos(updatedProductos); // Propaga el cambio al componente padre
    };

    const handleAddRow = () => {
        setIsAddingRow(true);
        setLocalProductos(prevProductos => [
            ...prevProductos,
            {
                id: Date.now(),
                referencia: '',
                nomProducto: '',
                cantidad: 1,
                valorUnitario: 0,
                autoCompleted: false,
                stockErrorMessage: '',  // Inicializamos también aquí

            }
        ]);
        setTimeout(() => setIsAddingRow(false), 100); // Ajustar el tiempo si es necesario
    };
     const handleRemoveRow = (id) => {
        setLocalProductos(prevProductos => {
            const updatedProductos = prevProductos.filter(p => p.id !== id);
            setProductos(updatedProductos); // Actualiza el estado en el componente padre
            return updatedProductos;
        });
    };

    const handleChange = useCallback((id, name, value) => {
        if (name === 'cantidad' || name === 'valorUnitario') {
            value = parseFloat(value) || 0;
        }

        const updatedProductos = localProductos.map(p =>
            p.id === id ? { ...p, [name]: value } : p
        );

        if (name === 'cantidad') {
            const producto = updatedProductos.find(p => p.id === id); // Obtenemos la versión actualizada

            setTimeout(async () => {
                if (producto && producto.idProducto) {
                    try {
                        const response = await APIserver.get(`/api/productos/${producto.idProducto}`);
                        const cantidadDisponible = response.data.stock;

                        // Comprobamos si la cantidad ingresada excede la cantidad disponible
                        if (parseInt(value) > cantidadDisponible) {
                            const errorMessage = `La cantidad pedida (${value}) excede el stock, se ajusto a (${cantidadDisponible}).`;

                            // Ajustamos la cantidad al stock disponible y actualizamos el mensaje de error
                            const adjustedProductos = updatedProductos.map(p =>
                                p.id === id ? { ...p, cantidad: cantidadDisponible, stockErrorMessage: errorMessage } : p
                            );

                            // Actualizamos el estado local con la cantidad ajustada
                            setLocalProductos(adjustedProductos);
                            setProductos(adjustedProductos);  // Se envía el valor ajustado
                        } else {
                            // Limpiamos el mensaje de error si es válido
                            const resetErrorProductos = updatedProductos.map(p =>
                                p.id === id ? { ...p, stockErrorMessage: '' } : p
                            );

                            // Actualizamos el estado con la cantidad correcta
                            setLocalProductos(resetErrorProductos);
                            setProductos(resetErrorProductos);
                        }
                    } catch (error) {
                        console.error('Error al verificar stock del producto:', error);
                    }
                }
            }, 0);
        } else {
            // Si el campo no es "cantidad", simplemente actualizamos
            setProductos(updatedProductos);  // Actualizamos el estado padre
        }

        // Actualizamos el estado local
        setLocalProductos(updatedProductos);
    }, [localProductos, setProductos]);
    const handleKeyDown = async (e, id) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const producto = localProductos.find(p => p.id === id);
            if (producto && producto.referencia) {
                try {
                    // Obtener los datos del producto según la referencia
                    const response = await APIserver.get(`/api/referencia/${producto.referencia}`);
                    const product = response.data;
    
                    if (product) {
                        const stockResponse = await APIserver.get(`/api/productos/${product.idProducto}`);
                        const stockDisponible = stockResponse.data ? (stockResponse.data.stock ?? 0) : 0;
                        const unitPrice = tipoFactura === 'distribucion' ? product.precioM : product.precioU;
                        const cantidadActual = producto.cantidad || 1;

                        if (cantidadActual > stockDisponible && stockDisponible > 0) {
                            // Ajustar la cantidad al stock disponible
                            setLocalProductos(prevProductos =>
                                prevProductos.map(p =>
                                    p.id === id ? {
                                        ...p,
                                        referencia: product.referencia,
                                        nomProducto: product.nomProducto,
                                        valorUnitario: parseFloat(unitPrice) || 0,
                                        descuento: 0,
                                        idProducto: product.idProducto,
                                        stockDisponible: stockDisponible,
                                        cantidad: stockDisponible,
                                        stockErrorMessage: `La cantidad pedida (${cantidadActual}) excede el stock, se ajustó a (${stockDisponible}).`,
                                        autoCompleted: true
                                    } : p
                                )
                            );
                        } else {
                            setLocalProductos(prevProductos =>
                                prevProductos.map(p =>
                                    p.id === id ? {
                                        ...p,
                                        referencia: product.referencia,
                                        nomProducto: product.nomProducto,
                                        valorUnitario: parseFloat(unitPrice) || 0,
                                        descuento: 0,
                                        idProducto: product.idProducto,
                                        stockDisponible: stockDisponible,
                                        cantidad: cantidadActual,
                                        stockErrorMessage: '',
                                        autoCompleted: true
                                    } : p
                                )
                            );
                        }
                    }
                } catch (error) {
                    console.error('Error al buscar el producto por referencia:', error);
                }
            }
        }
    };

    const handleSearch = (id) => {
        setCurrentProductId(id);
        setShowModal(true);
    };

    const handleSearchQueryChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (query.length > 0) {
            try {
                const response = await APIserver.get(`/api/info/productos`, {
                    params: { nombre: query, tipoFactura }
                });
                setSearchResults(response.data);
            } catch (error) {
                console.error('Error al buscar productos:', error);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectProduct = (product) => {
        setLocalProductos(prevProductos => {
            return prevProductos.map(p =>
                p.id === currentProductId
                    ? {
                        ...p,
                        referencia: product.referencia,
                        nomProducto: product.nomProducto,
                        valorUnitario: tipoFactura === 'distribucion' ? product.precioM : product.precioU,
                        descuento: 0,
                        idProducto: product.idProducto,
                        stockDisponible: product.stock,
                        cantidad: p.cantidad > 0 ? p.cantidad : 1,
                        autoCompleted: true
                    }
                    : p
            );
        });

        setShowModal(false);
    };

    const handleEnvioChange = (e) => {
        const value = e.target.value;
        setLocalEnvio(value === '' ? '' : (parseFloat(value) || 0));
    };

    const handleDescuentoChange = (e) => {
        const descuentoValue = parseFloat(e.target.value);
        setLocalDescuento(descuentoValue);
    };

    const calculateSubtotal = useCallback(() => {
        return localProductos.reduce((total, producto) => {
            const cantidad = parseFloat(producto.cantidad) || 0;
            const valorUnitario = parseFloat(producto.valorUnitario) || 0;
            const productoTotal = cantidad * valorUnitario;
            return total + productoTotal;
        }, 0).toFixed(2);
    }, [localProductos]);

    const calculateTotalItems = useCallback(() => {
        return localProductos.reduce((total, producto) => {
            const cantidad = parseInt(producto.cantidad, 10);
            return total + (isNaN(cantidad) ? 0 : cantidad);
        }, 0);
    }, [localProductos]);


    const calculateTotal = useCallback(() => {
        const subtotal = parseFloat(calculateSubtotal()) || 0;
        const descuentoTotal = subtotal * (parseFloat(localDescuento) || 0) / 100;
        const envio = parseFloat(localEnvio) || 0;
        return (subtotal + envio - descuentoTotal).toFixed(2);
    }, [calculateSubtotal, localEnvio, localDescuento]);

    const consolidateDuplicatedReferences = () => {
        setLocalProductos(prevProductos => {
            // Crear un mapa para agrupar productos por referencia.
            const referenceMap = {};
            let hasChanges = false;
    
            prevProductos.forEach(producto => {
                if (producto.referencia) {
                    if (!referenceMap[producto.referencia]) {
                        referenceMap[producto.referencia] = { ...producto }; // Clonar el producto inicial
                    } else {
                        // Si ya existe, sumar cantidades
                        const cantidadOriginal = referenceMap[producto.referencia].cantidad;
                        referenceMap[producto.referencia].cantidad += producto.cantidad;
    
                        // Validar si excede el stock
                        if (referenceMap[producto.referencia].cantidad > producto.stockDisponible) {
                            referenceMap[producto.referencia].cantidad = producto.stockDisponible;
                            AlertsFacturas.showStockAlert(producto.stockDisponible); // Mostrar alerta
                        }
    
                        // Detectar cambios
                        if (referenceMap[producto.referencia].cantidad !== cantidadOriginal) {
                            hasChanges = true;
                        }
                    }
                } else {
                    // Si no tiene referencia, agregar directamente
                    referenceMap[producto.id] = { ...producto };
                }
            });
    
            const updatedProductos = Object.values(referenceMap);
    
            // Evitar actualizaciones innecesarias
            if (JSON.stringify(updatedProductos) === JSON.stringify(prevProductos)) {
                return prevProductos;
            }
    
            return updatedProductos;
        });
    };
    
    // Autocompletar de forma asíncrona productos que tengan referencia pero no tengan detalles completos
    useEffect(() => {
        if (isAddingRow) return;
        const productosIncompletos = localProductos.filter(p => p.referencia && !p.autoCompleted && (!p.valorUnitario || !p.nomProducto));
        if (productosIncompletos.length === 0) return;

        let isMounted = true;
        const resolverIncompletos = async () => {
            try {
                const actualizados = await Promise.all(
                    localProductos.map(async (producto) => {
                        if (producto.referencia && !producto.autoCompleted && (!producto.valorUnitario || !producto.nomProducto)) {
                            try {
                                const response = await APIserver.get(`/api/referencia/${producto.referencia}`);
                                const product = response.data;
                                if (product) {
                                    const stockResponse = await APIserver.get(`/api/productos/${product.idProducto}`);
                                    const stockDisponible = stockResponse.data?.stock ?? 0;
                                    const unitPrice = tipoFactura === 'distribucion' ? product.precioM : product.precioU;
                                    const cantidadAjustada = producto.cantidad > stockDisponible && stockDisponible > 0 
                                        ? stockDisponible 
                                        : (producto.cantidad || 1);

                                    return {
                                        ...producto,
                                        referencia: product.referencia,
                                        nomProducto: product.nomProducto,
                                        valorUnitario: parseFloat(unitPrice) || 0,
                                        idProducto: product.idProducto,
                                        stockDisponible: stockDisponible,
                                        cantidad: cantidadAjustada,
                                        stockErrorMessage: producto.cantidad > stockDisponible
                                            ? `La cantidad pedida (${producto.cantidad}) excede el stock (${stockDisponible}).`
                                            : '',
                                        autoCompleted: true
                                    };
                                }
                            } catch (err) {
                                console.error('Error al resolver producto por referencia:', err);
                            }
                        }
                        return producto;
                    })
                );

                if (isMounted) {
                    setLocalProductos(actualizados);
                    setProductos(actualizados);
                }
            } catch (err) {
                console.error('Error en resolverIncompletos:', err);
            }
        };

        resolverIncompletos();
        return () => { isMounted = false; };
    }, [localProductos, tipoFactura, isAddingRow, setProductos]);
    
    
    useEffect(() => {
        const subtotal = parseFloat(calculateSubtotal()) || 0;
        const totalItems = calculateTotalItems();
        const total = parseFloat(calculateTotal()) || 0;

        setSubtotal(subtotal);
        setTotal(total);
        setCantItem(totalItems);
    }, [localProductos, localEnvio, localDescuento, calculateSubtotal, calculateTotal, calculateTotalItems, setSubtotal, setTotal, setCantItem]);


    return (
        <Card className='mt-2 shadow-none'>
            <Card.Header className='fw-bold'>Productos</Card.Header>
            <Table responsive className='shadow-none text-center'>
                <thead className='bg-primary-subtle'>
                    <tr>
                        <th>Referencia</th>
                        <th>Nombre</th>
                        <th>Cant.</th>
                        <th>Valor Unitario</th>
                        <th>Total</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {localProductos.map(producto => (
                        <ProductoRow
                            key={producto.id}
                            producto={producto}
                            onChange={handleChange}
                            onRemove={handleRemoveRow}
                            onSearch={handleSearch}
                            onKeyDown={handleKeyDown}
                            onBlurCantidad={handleChangeCantidadDirecta}
                            stockErrorMessage={stockErrorMessage} // Pasar el mensaje de error aquí

                        />

                    ))}

                    <tr>
                        <td colSpan="7" className='text-start'>
                            <Button variant='outline-success' onClick={handleAddRow}>
                                <PlusCircleFill size={18} className='me-2' /> Agregar Línea
                            </Button>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="5" className='text-end fw-bold'>Subtotal:</td>
                        <td colSpan="2" className='text-start'>{formatCOP(calculateSubtotal())}</td>
                    </tr>
                    <tr>
                        <td colSpan="5" className='text-end fw-bold'>Descuento:</td>
                        <td colSpan="2" className='text-start'>
                            <InputGroup size='sm'>
                                <InputGroup.Text>%</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={descuento}
                                    onChange={handleDescuentoChange}
                                    placeholder="Descuento"
                                />
                            </InputGroup>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="5" className='text-end fw-bold'>Items:</td>
                        <td colSpan="2" className='text-start'>{calculateTotalItems()}</td>
                    </tr>
                    <tr>
                        <td colSpan="5" className='text-end fw-bold'>Envio:</td>
                        <td colSpan="2" className='text-start'>
                            <InputGroup size='sm'>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={localEnvio}
                                    onChange={handleEnvioChange}
                                    placeholder="Envio"
                                />
                            </InputGroup>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="5" className='text-end fw-bold'>Total:</td>
                        <td colSpan="2" className='text-start'>{formatCOP(calculateTotal())}</td>
                    </tr>
                </tbody>

            </Table>

            <ProductSearchModal
                showModal={showModal}
                setShowModal={setShowModal}
                searchQuery={searchQuery}
                handleSearchQueryChange={handleSearchQueryChange}
                searchResults={searchResults}
                formatCOP={formatCOP}
                handleSelectProduct={handleSelectProduct}
            />
        </Card>
    );
};

export default Productos;
